import { round } from 'lodash';
import { MapNode, MapNodeType, PipeMapNode } from '../mapNode';
import { PressureStats, TreeNodeType, TreeNodeWithChildren } from '../treeNode';
import { MapFromTree } from './MapFromTree';
import { TreeWithStats } from './treeHelpers';
import { TreeStatus } from './treeValidator';

export const NODE_HEIGHT = 204;
export const NODE_WIDTH = 182;
export const NODE_WIDTH_SPACING = 64;
export const NODE_HEIGHT_SPACING = 59;

const X_MULTIPLIER = NODE_WIDTH + NODE_WIDTH_SPACING;
const Y_MULTIPLIER = NODE_HEIGHT + NODE_HEIGHT_SPACING;

export const SMALL_NODE_WIDTH_SCALING = 1.75;
const SMALL_NODE_HEIGHT_SCALING = 3;
export const generateMapFromTree = (tree: TreeWithStats, size: 'Small' | 'Large' = 'Large', styleEdges = true) => {
  const mapFromTree = new MapFromTree(tree, styleEdges);
  return { map: multiplyMap(mapFromTree.map, size), edges: mapFromTree.edges };
};

const multiplyMap = (nodes: Array<MapNode>, size: 'Small' | 'Large'): Array<MapNode> => {
  const xMultiplier = size === 'Small' ? X_MULTIPLIER / SMALL_NODE_WIDTH_SCALING : X_MULTIPLIER;
  const yMultiplier = size === 'Small' ? Y_MULTIPLIER / SMALL_NODE_HEIGHT_SCALING : Y_MULTIPLIER;

  return nodes.map(node => ({
    ...node,
    position: { x: node.position.x * xMultiplier, y: node.position.y * yMultiplier },
  }));
};

export const pipeMapNodeFromTreeNodeParent = (
  parentNode: TreeNodeWithChildren,
  depth: number,
  x: number,
  y: number,
  isFirstChild: boolean,
): PipeMapNode => {
  const id = getMapId(depth, x, y);

  const [pipeStats, markedForDeletion] =
    parentNode.nodeType === TreeNodeType.Junction
      ? isFirstChild
        ? [parentNode.stats.firstChildExitPipe, parentNode.firstChild.exitPipe.markedForDeletion]
        : [parentNode.stats.secondChildExitPipe, parentNode.secondChild.exitPipe.markedForDeletion]
      : [parentNode.stats.exitPipe, parentNode.firstChild.exitPipe.markedForDeletion];

  return {
    id,
    data: { parentTreeNode: parentNode, isFirstChild, ...pipeStats, markedForDeletion },
    type: MapNodeType.Pipe,
    position: { x, y },
  };
};

export const getMapDisplayStatus = (status: TreeStatus) => {
  if (status.incompleteReasons.length > 0) {
    return { status: 'Map Incomplete', showErrorIndicator: true, errorMessage: status.incompleteReasons.join(`. `) };
  } else if (status.hasError) {
    return {
      status: 'Map Error',
      showErrorIndicator: true,
      errorMessage: 'One or more elements are in an error state',
    };
  }

  return { status: 'Map Complete', showErrorIndicator: false, errorMessage: null };
};

export const getMapId = (depth: number, x: number, y: number) => (y * depth + x).toString();

export const generatedPressureString = (generatedPressureInKilopascals: number | null) =>
  generatedPressureInKilopascals ? `Generated: ${round(generatedPressureInKilopascals)} kPa` : '';

export const getCapacityString = ({
  requiredPressureInKilopascals,
  availablePressureInKilopascals,
  pumpCapacityPercentage,
}: PressureStats) =>
  requiredPressureInKilopascals && availablePressureInKilopascals && pumpCapacityPercentage
    ? `Capacity: ${round(requiredPressureInKilopascals, 1)} /
     ${round(availablePressureInKilopascals, 1)} kPa (${round(pumpCapacityPercentage)}%)`
    : '';

export const getFlowRateString = (
  flowRateInLitresPerMinute: number | null,
  velocityInMetresPerSecond: number | null,
) => {
  if (!flowRateInLitresPerMinute) {
    return '';
  }

  const flowRate = `${round(flowRateInLitresPerMinute, 1)} l/min`;

  if (!velocityInMetresPerSecond) {
    return flowRate;
  }

  return `${flowRate} @ ${round(velocityInMetresPerSecond, 2)} m/s`;
};

export const getPressureDropString = (pressureDropInKilopascals: number | null) =>
  pressureDropInKilopascals ? `Pressure Drop: ${round(pressureDropInKilopascals, 2)} kPa` : '';
